import { useState, useRef, useEffect, useCallback } from 'react';
import MicIcon from '@/assets/common/mic.svg?react';
import PlayIcon from '@/assets/common/play.svg?react';
import PauseIcon from '@/assets/common/pause.svg?react';
import TrashIcon from '@/assets/common/trash.svg?react';
import styles from './AudioRecorder.module.scss';

interface AudioRecorderProps {
  onRecordingComplete: (blob: Blob, durationSeconds: number) => void;
  disabled?: boolean;
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

export function AudioRecorder({ onRecordingComplete, disabled = false }: AudioRecorderProps) {
  const [status, setStatus] = useState<'idle' | 'requesting' | 'recording' | 'recorded'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [analyserData, setAnalyserData] = useState<number[]>(new Array(32).fill(0));

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const animationRef = useRef<number | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const startTimeRef = useRef<number>(0);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopRecording();
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
    };
  }, []);

  // Visualizer update
  const updateVisualizer = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    // Sample 32 values from the frequency data
    const samples: number[] = [];
    const step = Math.floor(dataArray.length / 32);
    for (let i = 0; i < 32; i++) {
      samples.push(dataArray[i * step] / 255);
    }
    setAnalyserData(samples);

    if (status === 'recording') {
      animationRef.current = requestAnimationFrame(updateVisualizer);
    }
  }, [status]);

  const startRecording = async () => {
    setError(null);
    setStatus('requesting');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      streamRef.current = stream;

      // Set up audio context and analyser for visualization
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Determine supported MIME type
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/mp4';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = '';
          }
        }
      }

      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        setRecordedBlob(blob);

        const url = URL.createObjectURL(blob);
        setRecordedUrl(url);
        setStatus('recorded');
      };

      mediaRecorder.start(100); // Collect data every 100ms
      startTimeRef.current = Date.now();
      setStatus('recording');
      setRecordingTime(0);

      // Start timer
      timerRef.current = window.setInterval(() => {
        setRecordingTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);

      // Start visualizer
      animationRef.current = requestAnimationFrame(updateVisualizer);

    } catch (err) {
      console.error('Error accessing microphone:', err);
      setStatus('idle');

      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          setError('Microphone access denied. Please allow microphone access in your browser settings.');
        } else if (err.name === 'NotFoundError') {
          setError('No microphone found. Please connect a microphone and try again.');
        } else {
          setError('Failed to access microphone. Please try again.');
        }
      } else {
        setError('Failed to start recording. Please try again.');
      }
    }
  };

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setAnalyserData(new Array(32).fill(0));
  };

  const handleStopRecording = () => {
    stopRecording();
  };

  const handlePlayPause = () => {
    const audio = previewAudioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const handleDiscard = () => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordingTime(0);
    setStatus('idle');
    setIsPlaying(false);
  };

  const handleUseRecording = () => {
    if (recordedBlob) {
      onRecordingComplete(recordedBlob, recordingTime);
    }
  };

  // Preview audio handlers
  useEffect(() => {
    const audio = previewAudioRef.current;
    if (!audio) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [recordedUrl]);

  return (
    <div className={styles.recorder}>
      {error && (
        <div className={styles.recorder__error}>{error}</div>
      )}

      {status === 'idle' && (
        <button
          className={styles.recorder__startButton}
          onClick={startRecording}
          disabled={disabled}
        >
          <MicIcon />
          <span>Start Recording</span>
        </button>
      )}

      {status === 'requesting' && (
        <div className={styles.recorder__requesting}>
          <div className={styles.recorder__spinner} />
          <span>Requesting microphone access...</span>
        </div>
      )}

      {status === 'recording' && (
        <div className={styles.recorder__recording}>
          <div className={styles.recorder__visualizer}>
            {analyserData.map((value, index) => (
              <div
                key={index}
                className={styles.recorder__bar}
                style={{ height: `${Math.max(4, value * 100)}%` }}
              />
            ))}
          </div>

          <div className={styles.recorder__recordingInfo}>
            <span className={styles.recorder__recordingIndicator} />
            <span className={styles.recorder__time}>{formatTime(recordingTime)}</span>
          </div>

          <button
            className={styles.recorder__stopButton}
            onClick={handleStopRecording}
          >
            Stop Recording
          </button>
        </div>
      )}

      {status === 'recorded' && recordedUrl && (
        <div className={styles.recorder__preview}>
          <audio ref={previewAudioRef} src={recordedUrl} />

          <div className={styles.recorder__previewControls}>
            <button
              className={styles.recorder__playButton}
              onClick={handlePlayPause}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>

            <div className={styles.recorder__previewInfo}>
              <span className={styles.recorder__previewDuration}>
                Duration: {formatTime(recordingTime)}
              </span>
            </div>

            <button
              className={styles.recorder__discardButton}
              onClick={handleDiscard}
              title="Discard recording"
            >
              <TrashIcon />
            </button>
          </div>

          <button
            className={styles.recorder__useButton}
            onClick={handleUseRecording}
            disabled={disabled}
          >
            Use This Recording
          </button>
        </div>
      )}
    </div>
  );
}
